import {
    Assignment,
    Business,
    Insights,
    Leaderboard,
    Map,
    People,
    PropaneTank,
    Speed,
    Today,
} from '@mui/icons-material';
import _ from 'lodash';
import { DeviceReadingsDictionaryResult, DeviceReadingsResult } from 'types';

/**
 * Returns the deep value within an object by path name
 * @description Returns the value of an object given a path delimited by periods (IE: deep.nested.prop.name gives the value of "name" in the "deep" object)
 * @param obj object to be accessed
 * @param path path of the property to be accessed
 * @returns value from given path name of object
 */
export const getObjectValueByPath: any = (
    obj: Record<any, any>,
    path: string
) => {
    if (typeof obj !== 'object' || obj === null) return null;
    const pathProperties = path
        .replace(/\[(\w+)\]/g, '.$1') // convert indexes to properties
        .replace(/^\./, '') // remove trailing period if available
        .split('.'); // convert into array of properties

    for (const prop of pathProperties) {
        if (prop in obj) {
            obj = obj[prop];
        } else {
            return null;
        }
    }

    return obj;
};

/**
 * Returns battery level based on the battery crit and warn on a device.
 * @param batteryCrit battery crit of device battery
 * @param batteryWarn battery warn of device battery
 * @returns battery level based on the battery crit and warn
 */
export const getBatteryLevel = (
    batteryCrit: number | null,
    batteryWarn: number | null
) => {
    if (batteryCrit) {
        if (batteryCrit < 0) return 20;
        if (batteryWarn) {
            if (batteryWarn < 0 && batteryCrit >= 0) {
                return 60;
            } else if (batteryWarn > 0 && batteryCrit > 0) {
                return 100;
            }
        }
    }
    return 'unknown';
};

/**
 * Returns an object containing all attributes which are different between two objects
 * @param oldObj first object to be compared; older data
 * @param newObj second object to be compared; newer data
 * @returns {Object} diff between the two objects
 */
export const getObjectDiff = (oldObj: object, newObj: object) => {
    const changes = _.differenceWith(
        _.toPairs(newObj),
        _.toPairs(oldObj),
        _.isEqual
    );

    return _.fromPairs(changes);
};

/** Change undefined values to null. This is useful for some firebase updates. */
export const recursivelyNullifyUndefinedValues = (obj: any) => {
    Object.entries(obj).forEach(([key, value]) => {
        if (!!value && typeof value === 'object') {
            recursivelyNullifyUndefinedValues(value);
        } else if (value === undefined) {
            obj[key] = null;
        }
    });
    return obj;
};

export const copyToClipboard = (e: React.MouseEvent, dataToCopy: string) => {
    e.stopPropagation();
    if (navigator) {
        navigator.clipboard.writeText(dataToCopy);
    }
};

export const convertReadingsDictionaryToList = (
    input: DeviceReadingsDictionaryResult
): DeviceReadingsResult => {
    const output: DeviceReadingsResult = { data: [], count: 0 };
    for (const key in input) {
        const subOutput = { ...input[key] };
        subOutput._id = key;
        output.data.push(subOutput);
    }
    output.count = output.data.length;
    return output;
};

/**
 * Merges data as it appears in user session with a more current model, either adding missing items or removing ones that should no longer exist
 * Note: only operates on the uppermost layer of an object. Any nested items will remain as they are in the session model
 * @param sessionModel an array or object retrieved from session data
 * @param currentModel an array or object representing the up-to-date version of what exists in session
 * @returns {Array | Object}
 */
export const migrateSessionData = (
    sessionModel: any[] | { [x: string]: any },
    currentModel: any[] | { [x: string]: any }
): any[] | object => {
    const isSessionArray = _.isArray(sessionModel);
    const isCurrentArray = _.isArray(currentModel);

    // if type mismatch, assume the old model is no longer compatible whatsoever and override it
    if (isSessionArray !== isCurrentArray) {
        return currentModel;
    }

    // get array for determining differences
    const sessionModelKeys = isSessionArray
        ? sessionModel
        : Object.keys(sessionModel);
    const currentModelKeys = isCurrentArray
        ? currentModel
        : Object.keys(currentModel);

    const missingItems = _.difference(currentModelKeys, sessionModelKeys); // present in current model but not session
    // const extraneousItems = _.difference(sessionModelKeys, currentModelKeys); // present in session but not current model

    // if no difference, session model is fine as-is
    if (!missingItems.length) {
        return sessionModel;
    }

    const newSessionKeys = [...sessionModelKeys];

    // add missing keys in their appropriate spots
    _.forEach(missingItems, (item) => {
        const currentModelIndex = currentModelKeys.indexOf(item);
        // avoid assigning values to out-of-bounds indeces
        if (currentModelIndex >= newSessionKeys.length) {
            newSessionKeys.push(item);
        } else {
            newSessionKeys.splice(currentModelIndex, 0, item);
        }
    });

    // remove keys that are no longer in the model
    // _.forEach(extraneousItems, (item) => {
    //     _.remove(newSessionKeys, (k) => k === item);
    // });

    // return array of values for sorting
    if (isSessionArray || isCurrentArray) {
        return newSessionKeys;
    }

    // or an object with the missing values / without extraneous ones for other models
    return _.reduce(
        newSessionKeys,
        (newSessionObj, currKey) => {
            return {
                ...newSessionObj,
                [currKey]: sessionModel[currKey] ?? currentModel[currKey],
            };
        },
        {}
    );
};

export const getMenuTabs = (supplierId?: string) => {
    return [
        {
            label: 'Live Reporting',
            icon: Leaderboard,
            value: '/live-reporting',
        },
        {
            label: 'Accounts',
            icon: PropaneTank,
            value: '/accounts',
        },
        ...(supplierId?.toString() === '0'
            ? []
            : [
                  {
                      label: 'Efficiency',
                      icon: Insights,
                      value: '/efficiency/trends',
                  },
              ]),
        {
            label: 'Delivery Planning',
            icon: Today,
            value: '/planning/list',
        },
        {
            label: 'Consumers',
            icon: People,
            value: '/consumers',
        },
        {
            label: 'Issues',
            icon: Assignment,
            value: '/issues',
        },
        {
            label: 'Portfolio',
            icon: Speed,
            value: '/monitors/overview',
        },
        {
            label: 'Map',
            icon: Map,
            value: '/map',
        },
        {
            label: 'Team',
            icon: People,
            value: '/team',
        },
        {
            label: 'Organization',
            icon: Business,
            value: '/organization',
        },
        // {
        //     label: 'Billing',
        //     icon: <AttachMoney />,
        //     value: '/billing',
        // },
        // {
        //     label: 'Inventory',
        //     icon: <OilBarrel />,
        //     value: '/inventory',
        // },
        // {
        //     label: 'Vehicles',
        //     icon: <LocalShipping />,
        //     value: '/vehicles',
        // },
    ];
};
